/** Label/value pair used by Oasis `Select` components. */
export interface SelectOption {
  label: string;
  value: string;
}

/**
 * Resolves a raw API value to a matching Select option value.
 * Tries exact value, case-insensitive value, then case-insensitive label.
 *
 * @param options - Dropdown options for the field
 * @param rawValue - Value from the API row
 * @param fallback - Value when rawValue is empty or unmatched
 * @returns The resolved option value
 */
export const resolveSelectOptionValue = (
  options: SelectOption[],
  rawValue: string | null | undefined,
  fallback = "",
): string => {
  const trimmed = rawValue?.trim() ?? "";
  if (!trimmed) {
    return fallback;
  }

  const exactMatch = options.find((option) => option.value === trimmed);
  if (exactMatch) {
    return exactMatch.value;
  }

  const caseInsensitiveValueMatch = options.find(
    (option) => option.value.toLowerCase() === trimmed.toLowerCase(),
  );
  if (caseInsensitiveValueMatch) {
    return caseInsensitiveValueMatch.value;
  }

  const caseInsensitiveLabelMatch = options.find(
    (option) => option.label.toLowerCase() === trimmed.toLowerCase(),
  );
  if (caseInsensitiveLabelMatch) {
    return caseInsensitiveLabelMatch.value;
  }

  return trimmed;
};
